// app/src/lib/cashflow/utilities.js — utility bill trending (US-3.9).
//
// Utility = RecurringItem with category === 'utility'. We pull historical
// charges from plaidTransactions matching the recurring stream's vendor name
// and bucket by month. > 20% MoM increase triggers an info alert.

import { db } from '../db.js';
import { requireUserId } from '../procurement/util.js';

const SPIKE_THRESHOLD_PCT = 0.20;

/**
 * @returns {Promise<{ recurringId:string, label:string, months: Array<{ month:string, amountCents:number, deltaPct:number|null }> }[]>}
 */
export async function trends({ lookbackMonths = 12 } = {}) {
  const userId = await requireUserId();
  const recs = await db.recurringItems.where('userId').equals(userId).toArray();
  const utilities = recs.filter((r) => (r.category || '').toLowerCase() === 'utility');
  if (!utilities.length) return [];

  const since = new Date(); since.setUTCMonth(since.getUTCMonth() - lookbackMonths);
  const sinceIso = since.toISOString().slice(0, 10);
  const txs = await db.plaidTransactions
    .where('[userId+date]').between([userId, sinceIso], [userId, '9999-99-99'])
    .filter((t) => !t.pending && t.amountCents > 0)
    .toArray();

  return utilities.map((u) => {
    const norm = normalize(u.vendorName || u.label || '');
    const matching = txs.filter((t) => {
      const tn = normalize(t.merchantName || '');
      return tn === norm || tn.includes(norm) || norm.includes(tn);
    });
    const byMonth = new Map();
    for (const t of matching) {
      const m = t.date.slice(0, 7);
      byMonth.set(m, (byMonth.get(m) || 0) + t.amountCents);
    }
    const months = [...byMonth.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([month, amountCents], i, arr) => {
      const prev = i > 0 ? arr[i - 1][1] : null;
      return {
        month,
        amountCents,
        deltaPct: prev && prev > 0 ? (amountCents - prev) / prev : null,
      };
    });
    return { recurringId: u.recurringId, label: u.label || u.vendorName, months };
  });
}

/** Emit info alerts on >20% MoM spikes (deduped per recurring+month). */
export async function checkSpikes() {
  const userId = await requireUserId();
  const all = await trends();
  const emitted = [];
  for (const u of all) {
    for (const m of u.months) {
      if (m.deltaPct == null || m.deltaPct < SPIKE_THRESHOLD_PCT) continue;
      const existing = await db.alerts.where('userId').equals(userId)
        .filter((a) => a.kind === 'UTILITY_SPIKE' && a.meta?.recurringId === u.recurringId && a.meta?.month === m.month)
        .first();
      const row = {
        alertId: existing?.alertId || crypto.randomUUID(),
        userId,
        severity: 'info',
        kind: 'UTILITY_SPIKE',
        title: `${u.label}: ${m.month} bill up ${(m.deltaPct * 100).toFixed(0)}%`,
        body: `Charged $${(m.amountCents / 100).toFixed(2)} this month.`,
        createdAt: existing?.createdAt || Date.now(),
        dismissed: existing?.dismissed ?? 0,
        meta: { recurringId: u.recurringId, month: m.month, amountCents: m.amountCents, deltaPct: m.deltaPct },
      };
      await db.alerts.put(row);
      emitted.push(row);
    }
  }
  return emitted;
}

function normalize(s) { return (s || '').toLowerCase().replace(/[^a-z0-9]/g, ''); }
